import React, { useMemo } from "react";
import { useSpecialistProfile } from "../state/SpecialistProfileContext";

type ReferralMethodOption = {
  code: "FAX" | "EREFERRAL" | "PORTAL" | "REFFORM" | "PHONE";
  label: string;
  help?: string;
};

const METHOD_OPTIONS: ReferralMethodOption[] = [
  { code: "FAX", label: "Fax", help: "Referrals are accepted by fax." },
  {
    code: "EREFERRAL",
    label: "eReferral",
    help: "Referrals are accepted through an Ontario eReferral pathway.",
  },
  {
    code: "PORTAL",
    label: "Online portal",
    help: "Referrals are accepted through a clinic/hospital portal.",
  },
  {
    code: "REFFORM",
    label: "Referral form (PDF)",
    help: "You have a referral form PDF that referrers should use.",
  },
  {
    code: "PHONE",
    label: "Phone intake",
    help: "Referrals start with a phone intake process.",
  },
];

const TabReferralWaitTimes: React.FC = () => {
  const { formState, updateReferralField, referralErrors } = useSpecialistProfile();

  const routineWaitWeeks = formState.referral.routineWaitWeeks ?? "";
  const urgentWaitWeeks = formState.referral.urgentWaitWeeks ?? "";
  const otherNotes = formState.referral.otherNotes ?? "";

  const selectedCodes: string[] = useMemo(() => {
    const v = formState.referral.referralMethodCodes;
    return Array.isArray(v) ? v.map(String) : [];
  }, [formState.referral.referralMethodCodes]);

  // Option A: upload only after profile is saved and has a SpecialistID.
  const specialistId: number | null = useMemo(() => {
    const a = (formState as any)?.basic?.specialistId;
    const b = (formState as any)?.specialistId;
    const c = (formState as any)?.createdSpecialistId;
    const id = a ?? b ?? c;
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [formState]);

  const toggleMethod = (code: ReferralMethodOption["code"]) => {
    const set = new Set(selectedCodes);
    if (set.has(code)) set.delete(code);
    else set.add(code);
    updateReferralField("referralMethodCodes", Array.from(set));
  };

  const setRoutineWeeks = (val: string) => {
    updateReferralField("routineWaitWeeks", val);
  };

  const setUrgentWeeks = (val: string) => {
    updateReferralField("urgentWaitWeeks", val);
  };

  const setReferralNotes = (val: string) => {
    updateReferralField("otherNotes", val);
  };

  const fieldLabelStyle: React.CSSProperties = {
    display: "block",
    fontWeight: 700,
    marginBottom: 8,
  };

  const helpStyle: React.CSSProperties = {
    fontSize: 13,
    opacity: 0.75,
    marginTop: 6,
    lineHeight: 1.45,
  };

  const errorStyle: React.CSSProperties = {
    marginTop: 8,
    color: "crimson",
    fontWeight: 700,
    fontSize: 13,
  };

  const boxStyle: React.CSSProperties = {
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: 14,
    background: "#fafafa",
  };

  const optionStyle = (checked: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: checked ? "2px solid #0017a2" : "1px solid #e2e2e2",
    background: checked ? "#eef1ff" : "#fff",
    cursor: "pointer",
  });

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: 10,
    borderRadius: 10,
    border: "1px solid #d8d8d8",
    boxSizing: "border-box",
    background: "#fff",
  };

  const disabledBtnStyle: React.CSSProperties = {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid #d8d8d8",
    background: "#f3f3f3",
    color: "#666",
    cursor: "not-allowed",
    fontWeight: 700,
  };

  return (
    <div>
      <p style={{ marginBottom: 16 }}>
        Configure how referrals are accepted and provide estimated wait times.{" "}
        <strong>No email referrals</strong>.
      </p>

      {/* Referral methods */}
      <div style={{ marginBottom: 18 }}>
        <label style={fieldLabelStyle}>Referral method (select all that apply) *</label>

        <div style={boxStyle}>
          <div style={{ display: "grid", gap: 10 }}>
            {METHOD_OPTIONS.map((m) => {
              const checked = selectedCodes.includes(m.code);
              return (
                <div
                  key={m.code}
                  style={optionStyle(checked)}
                  onClick={() => toggleMethod(m.code)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") toggleMethod(m.code);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleMethod(m.code)}
                    style={{ marginTop: 3 }}
                  />
                  <div>
                    <div style={{ fontWeight: 800 }}>{m.label}</div>
                    {m.help ? <div style={helpStyle}>{m.help}</div> : null}
                  </div>
                </div>
              );
            })}
          </div>

          {referralErrors?.referralMethodCodes ? (
            <div style={errorStyle}>{referralErrors.referralMethodCodes}</div>
          ) : null}

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
            Tip: choose “Referral form (PDF)” only if you actually have a form to share.
          </div>
        </div>
      </div>

      {/* Referral form upload (Option A: only after save) */}
      <div style={{ marginBottom: 18 }}>
        <label style={fieldLabelStyle}>Referral form (PDF) (optional)</label>

        <div style={boxStyle}>
          {specialistId ? (
            <>
              <div style={{ marginBottom: 8 }}>
                Specialist ID: <strong>{specialistId}</strong>
              </div>

              <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>
                Upload will be enabled in the next step (we’ll wire the upload endpoint + storage path).
              </div>

              <button type="button" style={disabledBtnStyle} disabled>
                Upload referral form (PDF)
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 10 }}>
                Save the specialist profile first to enable referral form upload (Option A).
              </div>
              <button type="button" style={disabledBtnStyle} disabled>
                Upload referral form (PDF)
              </button>
            </>
          )}
        </div>
      </div>

      {/* Wait times */}
      <div style={{ marginBottom: 18 }}>
        <label style={fieldLabelStyle}>Estimated wait times (optional)</label>

        <div style={boxStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                Routine (weeks)
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={routineWaitWeeks}
                onChange={(e) => setRoutineWeeks(e.target.value)}
                placeholder="e.g., 8"
                style={inputStyle}
              />
              {referralErrors?.routineWaitWeeks ? (
                <div style={errorStyle}>{referralErrors.routineWaitWeeks}</div>
              ) : null}
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                Urgent (weeks)
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={urgentWaitWeeks}
                onChange={(e) => setUrgentWeeks(e.target.value)}
                placeholder="e.g., 2"
                style={inputStyle}
              />
              {referralErrors?.urgentWaitWeeks ? (
                <div style={errorStyle}>{referralErrors.urgentWaitWeeks}</div>
              ) : null}
            </div>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
            Leave blank if unknown. These values are displayed publicly on the specialist profile.
          </div>
        </div>
      </div>

      {/* Referral notes */}
      <div style={{ marginBottom: 10 }}>
        <label style={fieldLabelStyle}>Referral notes (optional)</label>
        <textarea
          value={otherNotes}
          onChange={(e) => setReferralNotes(e.target.value)}
          placeholder="e.g., Fax referral + consult note + relevant labs. Urgent referrals should be marked clearly."
          style={{ ...inputStyle, minHeight: 110, resize: "vertical" }}
        />
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
          Do not include any patient information.
        </div>
      </div>
    </div>
  );
};

export default TabReferralWaitTimes;
